import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject } from 'rxjs';
import { share, filter } from 'rxjs/operators';
import * as moment from 'moment';
import { RouterParamsService } from '@app/core/services';
import { HotelGroupsService } from '@app/core/services/hotel-groups';
import { MenuOptions, HotelGroup, SessionUser } from './menu-options.interface';
import { UserService } from '../user';
import { ClearStateService } from '../clear-state/clear-state.service';
import isEqual from 'lodash.isequal';
import { Params } from '@angular/router';
import { filterTruthy } from '@app/core/utils/rxjs.util';

const SERVER_FMT = 'YYYY-MM-DD';

@Injectable({
  providedIn: 'root',
})
export class MenuOptionsService {
  private _subject = new BehaviorSubject<MenuOptions | null>(null);
  private _user!: SessionUser;
  private _hotelGroup!: HotelGroup;
  private _todaysDate!: string;
  private _masterCopy!: MenuOptions | null;
  private _lastRouteParams!: Params;

  constructor(
    private _hotelGroupsService: HotelGroupsService,
    private _routerParamsService: RouterParamsService,
    private _userService: UserService,
    private _clearStateService: ClearStateService,
  ) {
    this._userService
      .getUser()
      .subscribe((user) => (this._user = user as SessionUser));
    this._hotelGroupsService
      .getHotelGroups()
      .pipe(filter((hotelGroups) => !!hotelGroups))
      .subscribe((hotelGroups) => {
        this._hotelGroup = hotelGroups?.[0]!;
        this._setDefaultMenuOptions();
      });

    // ROUTER SUBSCRIPTION TO AUTO UPDATE MENU-OPTIONS SERVICE
    this._routerParamsService.queryParams.subscribe((next) => {
      const params = next;
      if (!isEqual(this._lastRouteParams, params)) {
        this._updateDefaultProperties();

        if (params['d_s'] || params['d_e']) {
          this.setDateRange(params['d_s'], params['d_e']);
        }

        if (params['d']) {
          this.setDefaultDate(params['d']);
        }

        if (params['entId']) {
          this.setDefaultHotel(params['entId']);
        }

        if (params['d_w'] !== 'false' && params['d_w'] != null) {
          this.setWorkWeekDateRange();
        }

        if (
          params['d_wtd'] !== 'false' &&
          params['d_wtd'] != null &&
          params['wtd_start'] != null
        ) {
          this.setWeekToDateRange(params['wtd_start']);
        }
      }
      this._lastRouteParams = params;
    });

    this._clearStateService.clearState$.subscribe(() => this.clearState());
  }

  getMenuOptions(): Observable<MenuOptions> {
    return this._subject.asObservable().pipe(filterTruthy(), share());
  }

  setMenuOptions(value: MenuOptions | null): void {
    let newValue = null;
    if (value) {
      newValue = this.getMomentFmtMenuOptions(value);
    }
    this._masterCopy = structuredClone(newValue);
    this._subject.next(newValue);
  }

  private getMomentFmtMenuOptions(value: MenuOptions): MenuOptions {
    const newValue = structuredClone(value);
    if (newValue.date) {
      newValue.date = {
        today: newValue.date.today
          ? moment(newValue.date.today || '').format(SERVER_FMT)
          : newValue.date.today,
        tomorrow: newValue.date.tomorrow
          ? moment(newValue.date.tomorrow || '').format(SERVER_FMT)
          : newValue.date.tomorrow,
        yesterday: newValue.date.yesterday
          ? moment(newValue.date.yesterday || '').format(SERVER_FMT)
          : newValue.date.yesterday,
        default: newValue.date.default
          ? moment(newValue.date.default || '').format(SERVER_FMT)
          : newValue.date.default,
      };
    }

    if (newValue.dateRange) {
      newValue.dateRange = {
        end: newValue.dateRange.end
          ? moment(value.dateRange?.end).format(SERVER_FMT)
          : newValue.dateRange.end,
        start: newValue.dateRange.start
          ? moment(value.dateRange?.start).format(SERVER_FMT)
          : newValue.dateRange.start,
      };
    }

    if (newValue.minDate) {
      newValue.minDate = moment(value.minDate || '').format(SERVER_FMT);
    }

    if (newValue.maxDate) {
      newValue.maxDate = moment(value.maxDate || '').format(SERVER_FMT);
    }

    return newValue;
  }

  setDateRange(startDate: string, endDate: string): void {
    const menuOptions: MenuOptions | null = this._masterCopy;
    if (menuOptions?.dateRange) {
      menuOptions.dateRange = {
        start: startDate,
        end: endDate,
      };
    }

    this.setMenuOptions(menuOptions);
  }

  setWorkWeekDateRange(): void {
    const menuOptions: MenuOptions | null = this._masterCopy;
    if (menuOptions?.dateRange) {
      menuOptions.dateRange = {
        start: this._user.workWeekStartDate,
        end: this._user.workWeekEndDate,
      };
    }

    this.setMenuOptions(menuOptions);
  }

  setWeekToDateRange(hotelStartDate: string): void {
    const menuOptions: MenuOptions | null = this._masterCopy;
    if (menuOptions?.dateRange) {
      menuOptions.dateRange = {
        start: hotelStartDate,
        end: menuOptions.date?.yesterday ?? null,
      };
    }

    this.setMenuOptions(menuOptions);
  }

  setDefaultDate(date: string): void {
    if (this._masterCopy) {
      const menuOptions: MenuOptions = this._masterCopy;
      if (menuOptions.date)
        menuOptions.date.default = moment(date).format('YYYY-MM-DD');
      this.setMenuOptions(menuOptions);
    }
  }

  setDefaultHotel(entId: string): void {
    const menuOptions: MenuOptions | null = this._masterCopy;
    if (menuOptions?.hotel) {
      menuOptions.hotel = this._user.hotels.find(
        (item) => entId === item.entId,
      );
      this.setMenuOptions(menuOptions);
    }
  }

  private clearState(): void {
    this.setMenuOptions(null);
  }

  private _updateDefaultProperties() {
    const menuOptions: MenuOptions | null = this._masterCopy;
    if (!menuOptions) {
      return;
    }
    this._todaysDate = moment().format('YYYY-MM-DD');

    menuOptions.date = {
      today: this._todaysDate,
      tomorrow: moment(this._todaysDate).add(1, 'days').format('YYYY-MM-DD'),
      yesterday: moment(this._todaysDate)
        .subtract(1, 'days')
        .format('YYYY-MM-DD'),
      default: this._todaysDate,
    };

    menuOptions.dateRange = {
      end: moment(this._todaysDate).subtract(1, 'days').format('YYYY-MM-DD'),
      start: moment(this._todaysDate).subtract(1, 'days').format('YYYY-MM-DD'),
    };

    menuOptions.maxDate = moment(this._todaysDate)
      .add(1, 'y')
      .format('YYYY-MM-DD');

    menuOptions.minDate = moment('1996-01-01', 'YYYY-MM-DD').format(
      'YYYY-MM-DD',
    );

    this._masterCopy = structuredClone(menuOptions);
  }

  private _setDefaultMenuOptions(): void {
    this._todaysDate = moment().format('YYYY-MM-DD');

    const menuOptionsObject: MenuOptions = {
      date: {
        today: this._todaysDate,
        tomorrow: moment(this._todaysDate).add(1, 'days').format('YYYY-MM-DD'),
        yesterday: moment(this._todaysDate)
          .subtract(1, 'days')
          .format('YYYY-MM-DD'),
        default: this._todaysDate,
      },
      dateRange: {
        end: moment(this._todaysDate).subtract(1, 'days').format('YYYY-MM-DD'),
        start: moment(this._todaysDate)
          .subtract(1, 'days')
          .format('YYYY-MM-DD'),
      },
      maxDate: moment(this._todaysDate).add(1, 'y').format('YYYY-MM-DD'),
      minDate: moment('1996-01-01', 'YYYY-MM-DD').format('YYYY-MM-DD'),
      user: this._user,
      hotel: this._user.hotels[0],
      hotelGroup: this._hotelGroup,
      custom: {},
    };
    this.setMenuOptions(menuOptionsObject);
  }
}
